import { useCallback, useEffect, useState } from 'react';

import type {
  CupMatch,
  CupPrize,
  CupRankingArtist,
  MonthlyCup,
  MusicGenre,
  TrackPlatform,
} from '../types';
import { useMusicGenreOptions } from '../lib/music-genres';
import { SponsoredSlot } from './SponsoredSlot';

const platforms: Array<[TrackPlatform, string]> = [
  ['spotify', 'Spotify'],
  ['youtube', 'YouTube / YouTube Music'],
  ['apple_music', 'Apple Music'],
  ['soundcloud', 'SoundCloud'],
  ['deezer', 'Deezer'],
  ['tidal', 'Tidal'],
];
const cupFormBaseTime = Date.now();
const cupFormTomorrow = new Date(cupFormBaseTime + 24 * 60 * 60_000).toISOString().slice(0, 16);
const cupFormNextWeek = new Date(cupFormBaseTime + 7 * 24 * 60 * 60_000).toISOString().slice(0, 16);

type CupResponse = { cup: MonthlyCup | null; canManage: boolean };

export function MonthlyCupArena({ authenticated }: { authenticated: boolean }) {
  const [cup, setCup] = useState<MonthlyCup | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [ranking, setRanking] = useState<CupRankingArtist[]>([]);
  const [prizes, setPrizes] = useState<CupPrize[]>([]);
  const [submission, setSubmission] = useState<
    Record<string, { platform: TrackPlatform; url: string }>
  >({});

  const load = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/cups/current', { cache: 'no-store' });
      if (!response.ok) throw new Error('No fue posible abrir la Copa.');
      const body = (await response.json()) as CupResponse;
      setCup(body.cup);
      setCanManage(body.canManage);
      if (body.cup) {
        const prizeResponse = await fetch(`/api/v1/cups/${body.cup.slug}/prizes`, {
          cache: 'no-store',
        });
        if (prizeResponse.ok) {
          setPrizes(((await prizeResponse.json()) as { prizes: CupPrize[] }).prizes);
        }
      } else {
        setPrizes([]);
      }
      const rankingResponse = await fetch('/api/v1/rankings/cup', { cache: 'no-store' });
      if (rankingResponse.ok) {
        const rankingBody = (await rankingResponse.json()) as { artists: CupRankingArtist[] };
        setRanking(rankingBody.artists);
      }
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible abrir la Copa.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function action(url: string, options: RequestInit = {}) {
    setBusy(url);
    setMessage(null);
    try {
      const response = await fetch(url, options);
      const body = (await response.json().catch(() => ({}))) as { detail?: string };
      if (!response.ok) throw new Error(body.detail ?? 'No fue posible completar la acción.');
      await load();
      return true;
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'No fue posible completar la acción.');
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function register() {
    if (!cup) return;
    const done = await action(`/api/v1/cups/${cup.slug}/register`, { method: 'POST' });
    if (done) setMessage('Inscripción confirmada. Tu lugar en la Copa está reservado.');
  }

  async function submitSong(event: React.FormEvent, match: CupMatch) {
    event.preventDefault();
    if (!cup) return;
    const draft = submission[match.id] ?? { platform: 'spotify' as const, url: '' };
    const done = await action(`/api/v1/cups/${cup.slug}/matches/${match.id}/submissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platform: draft.platform,
        trackUrl: draft.url,
        producerCreditConfirmed: true,
      }),
    });
    if (done) setMessage('Canción entregada con la confirmación de créditos del productor.');
  }

  async function vote(match: CupMatch, selectedUserId: string) {
    if (!cup) return;
    const done = await action(`/api/v1/cups/${cup.slug}/matches/${match.id}/votes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selectedUserId }),
    });
    if (done) setMessage('Tu voto de Copa quedó registrado y no puede cambiarse.');
  }

  if (loading) {
    return (
      <section className="mt-24 text-center text-sm text-zinc-500">
        Preparando la Copa Mensual…
      </section>
    );
  }

  return (
    <section id="copa" className="mt-24 lg:mt-32">
      <div className="mb-7 flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="eyebrow">Copa mensual</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-5xl">
            Un beat. Dos artistas. Un lugar en la siguiente ronda.
          </h2>
          <p className="mt-4 max-w-3xl text-sm leading-6 text-zinc-400">
            Cada enfrentamiento exige la misma pista para ambos contrincantes. Las canciones se
            publican en sus plataformas oficiales y cada reproducción beneficia al artista.
          </p>
        </div>
      </div>

      {message && (
        <p
          role="status"
          className="mb-5 rounded-2xl border border-violet-300/15 bg-violet-300/5 px-4 py-3 text-sm text-violet-100"
        >
          {message}
        </p>
      )}

      {!cup ? (
        canManage ? (
          <CreateCupForm busy={Boolean(busy)} onCreated={load} setMessage={setMessage} />
        ) : (
          <div className="glass-panel rounded-[2rem] p-8 text-center">
            <p className="text-lg font-bold text-white">
              La próxima Copa todavía no fue publicada.
            </p>
            <p className="mt-2 text-sm text-zinc-500">
              Aquí aparecerán el premio, patrocinadores, reglas e inscripciones.
            </p>
          </div>
        )
      ) : (
        <>
          <CupHeader
            cup={cup}
            authenticated={authenticated}
            busy={Boolean(busy)}
            register={register}
            prizes={prizes}
          />
          {canManage && (
            <CupAdmin cup={cup} busy={busy} action={action} load={load} setMessage={setMessage} />
          )}
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <SponsoredSlot slot="cup_bracket" compact />
            <SponsoredSlot slot="cup_track" compact />
          </div>
          <div className="mt-7 overflow-x-auto pb-3">
            <div className="grid min-w-[760px] auto-cols-[minmax(250px,1fr)] grid-flow-col gap-4">
              {cup.rounds.map((round) => (
                <section
                  key={round.id}
                  className="rounded-[1.6rem] border border-white/8 bg-white/[0.025] p-4"
                >
                  <div className="mb-4">
                    <p className="text-[0.62rem] uppercase tracking-[0.2em] text-fuchsia-200/60">
                      Etapa {round.number}
                    </p>
                    <h3 className="mt-1 font-bold text-white">{round.name}</h3>
                    <span className="mt-2 inline-flex rounded-full border border-violet-300/15 bg-violet-300/5 px-2.5 py-1 text-[0.58rem] font-bold uppercase tracking-wider text-violet-200">
                      {round.genreLabel}
                    </span>
                  </div>
                  {round.track ? (
                    <div className="mb-4 rounded-xl border border-amber-300/15 bg-amber-300/[0.04] p-3">
                      <strong className="block text-xs text-amber-100">
                        {round.track.creditLine}
                      </strong>
                      <span className="mt-1 block text-[0.65rem] text-zinc-500">
                        {round.track.name}
                      </span>
                      {round.track.downloadUrl && (
                        <a
                          className="mt-2 inline-block text-xs font-bold text-amber-200 hover:text-white"
                          href={round.track.downloadUrl}
                        >
                          Descargar pista oficial ↓
                        </a>
                      )}
                    </div>
                  ) : (
                    <p className="mb-4 rounded-xl bg-white/[0.03] p-3 text-xs text-zinc-600">
                      Pista pendiente de publicación.
                    </p>
                  )}
                  <div className="space-y-4">
                    {round.matches.length ? (
                      round.matches.map((match) => (
                        <MatchCard
                          key={match.id}
                          match={match}
                          busy={Boolean(busy)}
                          draft={submission[match.id] ?? { platform: 'spotify', url: '' }}
                          setDraft={(draft) =>
                            setSubmission((current) => ({ ...current, [match.id]: draft }))
                          }
                          submitSong={submitSong}
                          vote={vote}
                        />
                      ))
                    ) : (
                      <p className="py-8 text-center text-xs text-zinc-600">
                        Bracket aún no generado.
                      </p>
                    )}
                  </div>
                </section>
              ))}
            </div>
          </div>
          <CupRanking artists={ranking} />
        </>
      )}
    </section>
  );
}

function CupRanking({ artists }: { artists: CupRankingArtist[] }) {
  return (
    <section className="mt-7 overflow-hidden rounded-[1.6rem] border border-white/8 bg-white/[0.025]">
      <div className="flex items-center justify-between border-b border-white/8 px-5 py-4">
        <div>
          <p className="text-sm font-bold text-white">Ranking histórico de Copa</p>
          <p className="mt-1 text-[0.62rem] text-zinc-600">Separado del Elo de batallas normales</p>
        </div>
        <span className="text-[0.6rem] uppercase tracking-wider text-zinc-600">
          Mérito deportivo
        </span>
      </div>
      {artists.length === 0 ? (
        <p className="px-5 py-7 text-center text-xs text-zinc-600">
          El historial comenzará con esta Copa.
        </p>
      ) : (
        <div className="divide-y divide-white/5">
          {artists.slice(0, 10).map((artist) => (
            <div
              key={artist.artistId}
              className="grid grid-cols-[2rem_1fr_auto_auto] items-center gap-3 px-5 py-3 text-xs"
            >
              <strong className="text-fuchsia-200">#{artist.rank}</strong>
              <span className="font-bold text-zinc-200">
                {artist.artisticName} <small className="text-zinc-600">{artist.countryCode}</small>
              </span>
              <span className="text-zinc-500">{artist.matchWins} victorias</span>
              <span className="font-bold text-amber-200">{artist.championships} copas</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CupHeader({
  cup,
  authenticated,
  busy,
  register,
  prizes,
}: {
  cup: MonthlyCup;
  authenticated: boolean;
  busy: boolean;
  register: () => void;
  prizes: CupPrize[];
}) {
  return (
    <div className="glass-panel overflow-hidden rounded-[2rem]">
      <div className="grid gap-7 p-6 sm:p-8 lg:grid-cols-[1fr_.72fr]">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-fuchsia-300/10 px-3 py-1 text-[0.62rem] font-bold uppercase tracking-wider text-fuchsia-200">
              {cup.status === 'registration'
                ? 'Inscripciones abiertas'
                : cup.status === 'active'
                  ? 'En competencia'
                  : 'Finalizada'}
            </span>
            <span className="text-xs text-zinc-600">{cup.edition}</span>
          </div>
          <h3 className="mt-4 text-3xl font-black text-white">{cup.title}</h3>
          <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-violet-200">
            {cupModeLabel(cup.mode)}
          </p>
          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-zinc-400">{cup.rules}</p>
          <div className="mt-5 rounded-xl border border-white/8 bg-black/15 p-4">
            <p className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-500">
              Géneros anunciados antes de la inscripción
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {cup.rounds.map((round) => (
                <span
                  key={round.id}
                  className="rounded-full bg-white/5 px-3 py-1.5 text-xs text-zinc-300"
                >
                  {round.name}: <strong className="text-white">{round.genreLabel}</strong>
                </span>
              ))}
            </div>
          </div>
          <p className="mt-5 text-xs text-zinc-500">
            {cup.registrationCount}/{cup.bracketSize} artistas · inscripción hasta{' '}
            {formatDate(cup.registrationEndsAt)}
          </p>
          {cup.canRegister && (
            <button
              disabled={busy}
              onClick={register}
              className="mt-4 rounded-full bg-white px-5 py-2.5 text-sm font-black text-zinc-950 hover:bg-fuchsia-200 disabled:opacity-40"
            >
              Inscribirme en la Copa
            </button>
          )}
          {!authenticated && cup.status === 'registration' && (
            <p className="mt-3 text-xs text-zinc-600">
              Inicia sesión con perfil de artista para inscribirte.
            </p>
          )}
          {cup.myRegistrationStatus && (
            <p className="mt-3 text-xs font-bold text-emerald-300">
              Tu estado: {registrationLabel(cup.myRegistrationStatus)}
            </p>
          )}
        </div>
        <div className="rounded-[1.5rem] border border-amber-300/15 bg-amber-300/[0.04] p-5">
          <p className="text-[0.65rem] uppercase tracking-[0.2em] text-amber-200">
            Premio anunciado
          </p>
          <strong className="mt-2 block text-xl text-white">{cup.prizeDescription}</strong>
          {prizes.length > 0 && (
            <div className="mt-4 space-y-2">
              {prizes.map((prize) => (
                <div key={prize.id} className="rounded-xl border border-white/8 bg-black/15 p-3">
                  <strong className="block text-sm text-white">{prize.title}</strong>
                  <span className="mt-1 block text-xs text-zinc-400">{prize.description}</span>
                  <span className="mt-2 block text-[0.62rem] uppercase tracking-wider text-amber-200">
                    {prize.amount_minor !== null && prize.currency
                      ? `${(prize.amount_minor / 100).toLocaleString('es', { minimumFractionDigits: 2 })} ${prize.currency} · `
                      : ''}
                    {prize.status === 'delivered' ? 'Entregado' : 'Anunciado'}
                    {prize.sponsor_name ? ` por ${prize.sponsor_name}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs leading-5 text-zinc-500">
            La entrega se coordina manualmente con el campeón. AGAM no ejecuta pagos automáticos en
            este sprint.
          </p>
          <div className="mt-5 border-t border-white/8 pt-4">
            <SponsoredSlot slot="cup_prize" compact />
          </div>
        </div>
      </div>
    </div>
  );
}

function MatchCard({
  match,
  busy,
  draft,
  setDraft,
  submitSong,
  vote,
}: {
  match: CupMatch;
  busy: boolean;
  draft: { platform: TrackPlatform; url: string };
  setDraft: (draft: { platform: TrackPlatform; url: string }) => void;
  submitSong: (event: React.FormEvent, match: CupMatch) => void;
  vote: (match: CupMatch, selectedUserId: string) => void;
}) {
  return (
    <article className="rounded-2xl border border-white/8 bg-zinc-950/65 p-3">
      <p className="mb-2 text-[0.58rem] uppercase tracking-wider text-zinc-600">
        Batalla {match.position}
      </p>
      {[match.artistA, match.artistB].map((artist, index) => {
        const submission = index === 0 ? match.submissions?.artistA : match.submissions?.artistB;
        const votes = index === 0 ? match.results?.artistA : match.results?.artistB;
        const winner = artist && match.winnerUserId === artist.id;
        return (
          <div
            key={index}
            className={`mb-2 rounded-xl border px-3 py-2 ${winner ? 'border-emerald-300/30 bg-emerald-300/5' : 'border-white/6 bg-white/[0.025]'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <strong className="text-sm text-zinc-200">{artist?.name ?? 'Por definir'}</strong>
              {votes !== undefined && (
                <span className="text-xs font-bold text-violet-200">{votes} votos</span>
              )}
            </div>
            {submission && (
              <a
                href={submission.trackUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-1 block text-[0.65rem] text-fuchsia-200 hover:text-white"
              >
                Escuchar en {submission.platform} ↗
              </a>
            )}
            {match.canVote && artist && (
              <button
                disabled={busy}
                onClick={() => vote(match, artist.id)}
                className="mt-2 rounded-full border border-violet-200/20 px-3 py-1 text-[0.65rem] font-bold text-violet-100 hover:bg-violet-200 hover:text-zinc-950 disabled:opacity-40"
              >
                Votar por {artist.name}
              </button>
            )}
          </div>
        );
      })}
      <p className="mt-2 text-[0.62rem] text-zinc-600">{matchStatus(match)}</p>
      {match.canSubmit && !match.mySubmission && (
        <form
          onSubmit={(event) => submitSong(event, match)}
          className="mt-3 space-y-2 border-t border-white/6 pt-3"
        >
          <select
            className="field-input text-xs"
            value={draft.platform}
            onChange={(event) =>
              setDraft({ ...draft, platform: event.target.value as TrackPlatform })
            }
          >
            {platforms.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <input
            className="field-input text-xs"
            type="url"
            required
            placeholder="Enlace oficial de tu canción"
            value={draft.url}
            onChange={(event) => setDraft({ ...draft, url: event.target.value })}
          />
          <p className="text-[0.6rem] leading-4 text-amber-100/70">
            Al entregar confirmas que utilizaste la pista oficial y acreditaste al productor.
          </p>
          <button
            disabled={busy || !draft.url}
            className="w-full rounded-full bg-violet-200 px-3 py-2 text-xs font-black text-zinc-950 disabled:opacity-40"
          >
            Entregar canción
          </button>
        </form>
      )}
      {match.mySubmission && (
        <p className="mt-2 text-[0.65rem] font-bold text-emerald-300">
          Tu entrega está registrada.
        </p>
      )}
    </article>
  );
}

function CupAdmin({
  cup,
  busy,
  action,
  load,
  setMessage,
}: {
  cup: MonthlyCup;
  busy: string | null;
  action: (url: string, options?: RequestInit) => Promise<boolean>;
  load: () => Promise<void>;
  setMessage: (value: string | null) => void;
}) {
  const [producer, setProducer] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [roundNumber, setRoundNumber] = useState(1);
  async function upload(event: React.FormEvent) {
    event.preventDefault();
    if (!file) return;
    const query = new URLSearchParams({ producer, filename: file.name });
    const response = await fetch(
      `/api/v1/admin/cups/${cup.slug}/rounds/${roundNumber}/track?${query}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'audio/mpeg' },
        body: file,
      },
    );
    const body = (await response.json()) as { detail?: string; creditLine?: string };
    if (!response.ok) return setMessage(body.detail ?? 'No fue posible cargar la pista.');
    setMessage(`${body.creditLine}. Archivo disponible para los participantes.`);
    setFile(null);
    await load();
  }
  return (
    <details className="mt-5 rounded-2xl border border-white/8 bg-white/[0.025] p-4">
      <summary className="cursor-pointer text-xs font-bold text-zinc-300">
        Control local de la Copa
      </summary>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <form onSubmit={upload} className="space-y-2 rounded-xl bg-black/20 p-3">
          <p className="text-xs font-bold text-white">Cargar pista por etapa</p>
          <select
            className="field-input text-xs"
            value={roundNumber}
            onChange={(event) => setRoundNumber(Number(event.target.value))}
          >
            {cup.rounds.map((round) => (
              <option key={round.id} value={round.number}>
                {round.name}
              </option>
            ))}
          </select>
          <input
            className="field-input text-xs"
            required
            placeholder="Nombre del productor"
            value={producer}
            onChange={(event) => setProducer(event.target.value)}
          />
          <input
            className="field-input text-xs"
            required
            type="file"
            accept="audio/*"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
          <button
            disabled={!file || !producer || Boolean(busy)}
            className="rounded-full bg-amber-200 px-4 py-2 text-xs font-black text-zinc-950 disabled:opacity-40"
          >
            Publicar pista y créditos
          </button>
        </form>
        <div className="rounded-xl bg-black/20 p-3">
          <p className="text-xs font-bold text-white">Bracket</p>
          <p className="mt-2 text-xs leading-5 text-zinc-500">
            Se habilita con {cup.bracketSize} inscritos y una pista cargada en la primera etapa.
          </p>
          <button
            disabled={Boolean(busy) || cup.status !== 'registration'}
            onClick={() =>
              void action(`/api/v1/admin/cups/${cup.slug}/generate-bracket`, { method: 'POST' })
            }
            className="mt-3 rounded-full bg-white px-4 py-2 text-xs font-black text-zinc-950 disabled:opacity-40"
          >
            Generar bracket aleatorio
          </button>
        </div>
      </div>
    </details>
  );
}

function CreateCupForm({
  busy,
  onCreated,
  setMessage,
}: {
  busy: boolean;
  onCreated: () => Promise<void>;
  setMessage: (value: string | null) => void;
}) {
  const genreOptions = useMusicGenreOptions();
  const [mode, setMode] = useState<MonthlyCup['mode']>('single_genre');
  const [bracketSize, setBracketSize] = useState<4 | 8 | 16 | 32>(4);
  const [genreCode, setGenreCode] = useState<MusicGenre>('salsa');
  const [roundGenres, setRoundGenres] = useState<MusicGenre[]>([
    'salsa',
    'merengue',
    'bolero',
    'rock_and_roll',
    'fusion',
  ]);
  const [fusionGenres, setFusionGenres] = useState<[MusicGenre, MusicGenre]>(['salsa', 'rock']);
  const totalRounds = Math.log2(bracketSize);

  async function create(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const response = await fetch('/api/v1/admin/cups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: data.get('title'),
        edition: data.get('edition'),
        bracketSize,
        mode,
        genreCode,
        roundGenres: mode === 'versatile' ? roundGenres.slice(0, totalRounds) : [],
        fusionGenres: mode === 'fusion' ? fusionGenres : undefined,
        registrationEndsAt: new Date(String(data.get('registrationEndsAt'))).toISOString(),
        startsAt: new Date(String(data.get('startsAt'))).toISOString(),
        prizeDescription: data.get('prizeDescription'),
        rules: data.get('rules'),
      }),
    });
    const body = (await response.json()) as { detail?: string };
    if (!response.ok) return setMessage(body.detail ?? 'No fue posible crear la Copa.');
    setMessage('Copa creada. Ahora carga la pista de la primera etapa.');
    await onCreated();
  }
  return (
    <form
      onSubmit={create}
      className="glass-panel grid gap-4 rounded-[2rem] p-6 sm:grid-cols-2 sm:p-8"
    >
      <div className="sm:col-span-2">
        <h3 className="text-xl font-bold text-white">Crear la primera Copa</h3>
        <p className="mt-1 text-xs text-zinc-500">
          En local, cualquier cuenta autenticada puede preparar la demostración administrativa.
        </p>
      </div>
      <Field label="Nombre">
        <input className="field-input" name="title" required defaultValue="Copa AGAM Mensual" />
      </Field>
      <Field label="Edición">
        <input className="field-input" name="edition" required defaultValue="Octubre 2026" />
      </Field>
      <Field label="Cupo">
        <select
          className="field-input"
          name="bracketSize"
          value={bracketSize}
          onChange={(event) => setBracketSize(Number(event.target.value) as 4 | 8 | 16 | 32)}
        >
          <option value="4">4 artistas</option>
          <option value="8">8 artistas</option>
          <option value="16">16 artistas</option>
          <option value="32">32 artistas</option>
        </select>
      </Field>
      <Field label="Modalidad de Copa">
        <select
          className="field-input"
          value={mode}
          onChange={(event) => setMode(event.target.value as MonthlyCup['mode'])}
        >
          <option value="single_genre">Un solo género</option>
          <option value="versatile">Versátil · cambia por etapa</option>
          <option value="fusion">Fusión · combina dos géneros</option>
        </select>
      </Field>
      {mode === 'single_genre' && (
        <Field label="Género de toda la Copa">
          <GenreSelect options={genreOptions} value={genreCode} onChange={setGenreCode} />
        </Field>
      )}
      {mode === 'versatile' && (
        <div className="grid gap-4 rounded-2xl border border-violet-300/15 bg-violet-300/[0.04] p-4 sm:col-span-2 sm:grid-cols-2">
          <p className="text-xs leading-5 text-zinc-400 sm:col-span-2">
            Los artistas conocerán esta ruta completa antes de inscribirse. La pista exacta se
            publica al comenzar cada etapa.
          </p>
          {Array.from({ length: totalRounds }, (_, index) => (
            <Field key={index} label={cupRoundName(index + 1, totalRounds)}>
              <GenreSelect
                options={genreOptions}
                value={roundGenres[index] ?? 'other'}
                onChange={(value) =>
                  setRoundGenres((current) => {
                    const next = [...current];
                    next[index] = value;
                    return next;
                  })
                }
              />
            </Field>
          ))}
        </div>
      )}
      {mode === 'fusion' && (
        <div className="grid gap-4 rounded-2xl border border-fuchsia-300/15 bg-fuchsia-300/[0.04] p-4 sm:col-span-2 sm:grid-cols-2">
          <p className="text-xs leading-5 text-zinc-400 sm:col-span-2">
            Cada pista deberá integrar los dos géneros elegidos. Ambos contrincantes seguirán usando
            exactamente la misma pista en su enfrentamiento.
          </p>
          <Field label="Primer género">
            <GenreSelect
              options={genreOptions}
              value={fusionGenres[0]}
              onChange={(value) => setFusionGenres([value, fusionGenres[1]])}
            />
          </Field>
          <Field label="Segundo género">
            <GenreSelect
              options={genreOptions}
              value={fusionGenres[1]}
              onChange={(value) => setFusionGenres([fusionGenres[0], value])}
            />
          </Field>
        </div>
      )}
      <Field label="Premio">
        <input
          className="field-input"
          name="prizeDescription"
          required
          defaultValue="$500 entregados por el patrocinador"
        />
      </Field>
      <Field label="Cierre de inscripción">
        <input
          className="field-input"
          type="datetime-local"
          name="registrationEndsAt"
          required
          defaultValue={cupFormTomorrow}
        />
      </Field>
      <Field label="Inicio">
        <input
          className="field-input"
          type="datetime-local"
          name="startsAt"
          required
          defaultValue={cupFormNextWeek}
        />
      </Field>
      <Field label="Reglas">
        <textarea
          className="field-input min-h-24"
          name="rules"
          required
          defaultValue="Ambos artistas usarán la pista oficial asignada. Deben acreditar al productor y entregar el enlace de su canción antes del plazo."
        />
      </Field>
      <button
        disabled={busy}
        className="rounded-full bg-white px-5 py-3 text-sm font-black text-zinc-950 disabled:opacity-40 sm:col-span-2"
      >
        Crear Copa Mensual
      </button>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="text-xs font-bold text-zinc-400">
      <span className="mb-2 block">{label}</span>
      {children}
    </label>
  );
}
function GenreSelect({
  options,
  value,
  onChange,
}: {
  options: readonly (readonly [string, string])[];
  value: MusicGenre;
  onChange: (value: MusicGenre) => void;
}) {
  return (
    <select
      className="field-input"
      value={value}
      onChange={(event) => onChange(event.target.value as MusicGenre)}
    >
      {options.map(([code, label]) => (
        <option key={code} value={code}>
          {label}
        </option>
      ))}
    </select>
  );
}
function cupRoundName(roundNumber: number, totalRounds: number) {
  const remaining = totalRounds - roundNumber;
  if (remaining === 0) return 'Final';
  if (remaining === 1) return 'Semifinal';
  if (remaining === 2) return 'Cuartos de final';
  if (remaining === 3) return 'Octavos de final';
  return `Ronda de ${2 ** (remaining + 1)}`;
}
function cupModeLabel(mode: MonthlyCup['mode']) {
  if (mode === 'versatile') return 'Copa Versátil · un género por etapa';
  if (mode === 'fusion') return 'Copa Fusión · dos géneros en una pista';
  return 'Copa de un solo género';
}
function formatDate(value: string) {
  return new Intl.DateTimeFormat('es-EC', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  );
}
function registrationLabel(value: string) {
  return (
    (
      {
        registered: 'Inscrito',
        active: 'Compitiendo',
        eliminated: 'Eliminado',
        champion: 'Campeón',
      } as Record<string, string>
    )[value] ?? value
  );
}
function matchStatus(match: CupMatch) {
  if (match.status === 'awaiting_submissions')
    return `Entregas hasta ${match.submissionDeadline ? formatDate(match.submissionDeadline) : 'fecha pendiente'}`;
  if (match.status === 'voting')
    return `Votación hasta ${match.votingEndsAt ? formatDate(match.votingEndsAt) : 'fecha pendiente'}${match.tieExtended ? ' · desempate extendido' : ''}`;
  if (match.status === 'tiebreak_required') return 'Empate persistente · revisión administrativa';
  if (match.status === 'closed') return 'Enfrentamiento finalizado';
  if (match.status === 'cancelled') return 'Cancelado por falta de entregas';
  return 'Esperando clasificados';
}
